import dayjs from 'dayjs';
// AES 加解密工具
export {
  AesEncryption,
  encryption,
  encryptECB,
  decryptECB,
  encryptByBase64,
  decodeByBase64,
  encryptByMd5,
  toUrlSafeBase64,
  fromUrlSafeBase64
} from './aes';
export type { EncryptionParams } from './aes';
export * from './courseHelper';
export * from './routeUtils';
export * from './weixin';
import { wxSdkUrl } from '@/config';
import { isAndroid } from './ua';

const YEAR_PATTER = 'YYYY年';
const DATE_PATTER = 'MM月DD日';
const TIME_PATTER = ' HH:mm';
const FULL_PATTER = YEAR_PATTER + DATE_PATTER + TIME_PATTER;
const EXCLUDE_YEAR_PATTER = DATE_PATTER + TIME_PATTER;
const YEAR_DATE_PATTER = YEAR_PATTER + DATE_PATTER;

export const stringifyObject = (obj: Record<string, any> = {}) => {
  return Object.keys(obj)
    .map(key => `${key}=${encodeURIComponent(obj[key])}`)
    .join('&');
}

export const getLabelByValue = (value: string, list: Record<string, any>[], findKey: string = 'value', labelKey: string = 'label') => {
  return list.find(item => item[findKey] === value)?.[labelKey] || '';
}

export function formatDate(
  timeStamp: number,
  formatStr: string = 'YYYY/MM/DD HH:mm:ss',
) {
  return dayjs(timeStamp).format(formatStr);
}

export function formatDateSecond(
  timeStamp: number,
  formatStr: string = 'YYYY/MM/DD HH:mm:ss',
) {
  return formatDate(timeStamp * 1000, formatStr);
}

export function formatDateSec(startTime: number, endTime: number) {
  const format = startTime === endTime ? 'HH:mm' : 'YYYY/MM/DD HH:mm';
  return `${formatDateSecond(startTime, 'YYYY/MM/DD HH:mm')} - ${formatDateSecond(endTime, format)}`;
}

// 获取课程详情的时间显示格式
export function getTimeRange(
  startPlay: any,
  endPlay: any,
  isShowAllWhenDiffYear: boolean,
) {
  if (startPlay === endPlay || startPlay === 0 || endPlay === 0) {
    return '';
  }
  //默认的为不是同一年的都显示完整的
  let startPatter = isShowAllWhenDiffYear ? FULL_PATTER : YEAR_DATE_PATTER;
  let endPatter = startPatter;
  const startCalendar = dayjs(startPlay);
  const endCalendar = dayjs(endPlay);
  //开始结束同一年
  if (startCalendar.year() === endCalendar.year()) {
    //本年
    const today = dayjs();
    const isCurrentYear = startCalendar.year() === today.year();
    //同一天的
    const isSameDay = startCalendar.isSame(endCalendar, 'day');
    //是本年，开始时间 不显示年 否则显示全路径
    startPatter = isCurrentYear ? EXCLUDE_YEAR_PATTER : FULL_PATTER;
    //是同一天的话，结束时间只显示 小时秒 否则显示月日
    endPatter = isSameDay ? TIME_PATTER : EXCLUDE_YEAR_PATTER;
    //新加的同一天
    startPatter = isSameDay ? startPatter : DATE_PATTER;
    endPatter = isSameDay ? endPatter : DATE_PATTER;
  }
  return (
    formatDateSecond(startPlay, startPatter) +
    ' - ' +
    formatDateSecond(endPlay, endPatter)
  );
}

// 获取中文数字映射
export function getChineseNumberMapping(num: number) {
  // 基本数字映射
  const numberMap: Record<string, string> = {
    0: '零',
    1: '一',
    2: '二',
    3: '三',
    4: '四',
    5: '五',
    6: '六',
    7: '七',
    8: '八',
    9: '九',
  };

  // 位数单位映射
  const unitMap: Record<string, string> = {
    0: '',
    1: '十',
    2: '百',
    3: '千',
    4: '万',
    8: '亿',
  };

  // 转换单个数字为中文
  function convertToChineseNumber(number: number) {
    if (number === 0) return numberMap[0];

    const numStr = number.toString();
    const len = numStr.length;

    return numStr.split('').reduce((result, digit, index) => {
      const chineseNum = numberMap[digit];
      const position = len - 1 - index;

      let unit = '';
      if (digit !== '0') {
        if (position >= 8) {
          unit = unitMap[8] + unitMap[position - 8];
        } else {
          unit = unitMap[position];
        }
      } else if (result.slice(-1) !== numberMap[0] && index !== len - 1) {
        return result + chineseNum;
      }

      return result + chineseNum + unit;
    }, '');
  }

  // 生成从0到num的映射对象
  const result: Record<string, string> = {};
  for (let i = 0; i <= num; i++) {
    result[i] = convertToChineseNumber(i);
  }

  return result;
}

export const loadWxSdk = () => {
  const script = document.createElement('script');
  script.src = wxSdkUrl;
  document.body.appendChild(script);
}

export const smoothElementScroll = (element: HTMLElement, target: number, duration = 1000, easing = 'easeOutQuad', cb: () => void = () => { }) => {
  // 安全检测
  if (!(element instanceof HTMLElement)) {
    throw new Error('第一个参数必须是DOM元素');
  }
  if (element.scrollHeight <= element.clientHeight) {
    console.warn('目标元素没有滚动条');
    return;
  }

  const start = element.scrollTop;
  const maxScroll = element.scrollHeight - element.clientHeight;
  const safeTarget = Math.max(0, Math.min(target, maxScroll));
  const distance = safeTarget - start;
  const startTime = performance.now();

  // 缓动函数集合
  const easingFunctions = {
    linear: (t: number) => t,
    easeInQuad: (t: number) => t * t,
    easeOutQuad: (t: number) => t * (2 - t),
    easeInOutQuad: (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  };

  // requestAnimationFrame 降级兼容
  const requestAnimFrame = (() => {
    return window.requestAnimationFrame ||
      (window as any).webkitRequestAnimationFrame ||
      (window as any).mozRequestAnimationFrame ||
      (window as any).oRequestAnimationFrame ||
      (window as any).msRequestAnimationFrame ||
      function (callback: FrameRequestCallback) {
        return window.setTimeout(() => {
          callback(performance.now());
        }, 1000 / 60); // 60fps
      };
  })();

  // 动画帧回调
  function scrollStep(timestamp: number) {
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easingFunctions[easing as keyof typeof easingFunctions](progress);

    element.scrollTop = start + distance * eased;

    if (progress >= 1) {
      // 确保滚动到目标位置
      element.scrollTop = safeTarget;
      if (cb) cb();
      return;
    }

    requestAnimFrame(scrollStep);
  }

  requestAnimFrame(scrollStep);
}

export const checkPassword = (password: string) => {
  // 检查密码长度
  if (password.length < 8 || password.length > 16) {
    return false;
  }

  // 检查是否包含不允许的字符（如汉字）
  const hasInvalidChars = /[^\w!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  if (hasInvalidChars) {
    return false;
  }

  // 检查字符类型
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  
  // 计算包含的字符类型数量
  const typeCount = [hasLowercase, hasUppercase, hasNumber, hasSpecial].filter(Boolean).length;
  
  // 必须包含至少三种类型
  return typeCount >= 3;
}

export const formateBridgeParams = (params: any) => {
  if (isAndroid()) {
    return JSON.stringify(params);
  }
  return params;
}

export const getEnv = () => {
  if (location.hostname.includes('dev')) {
    return 'dev';
  }
  if (location.hostname.includes('test')) {
    return 'test';
  }
  return 'prod';
}
